import React, { useEffect, useState } from 'react';
import { thirteenWeekCashFlowApi } from '../../helpers/thirteenWeekCashFlowApi';
import Chip from './Chip';
import { money, dateLabel } from './money';

const categories = [
  'operating_revenue',
  'other_inflow',
  'sales_tax',
  'ap_operating',
  'ap_inventory',
  'credit_card',
  'payroll',
  'owner_distribution',
  'debt',
  'other_outflow',
  'transfer',
  'uncategorized',
];

export default function CashActivityReviewTab({
  data,
  summary,
  loading,
  error,
  onRefresh,
}) {
  const [filters, setFilters] = useState({
    from: summary?.window?.start || '',
    to: summary?.window?.today || '',
    state: '',
    category: '',
    search: '',
  });
  const [selected, setSelected] = useState([]);
  const [ruleForm, setRuleForm] = useState(null);
  const [ruleError, setRuleError] = useState('');
  const rows = data?.data || data?.rows || [];
  useEffect(() => {
    if (summary?.window?.start && !filters.from) {
      setFilters((current) => ({
        ...current,
        from: summary.window.start,
        to: summary.window.today,
      }));
    }
  }, [summary?.window?.start, summary?.window?.today, filters.from]);
  useEffect(() => {
    const delay = filters.search ? 300 : 0;
    const timer = setTimeout(() => onRefresh(filters), delay);
    return () => clearTimeout(timer);
  }, [filters, onRefresh]);
  const update = async (row, payload) => {
    await thirteenWeekCashFlowApi.updateTransaction(
      row.plaidTransactionId,
      payload,
    );
    onRefresh(filters);
  };
  const openRuleForm = (row) => {
    setRuleError('');
    setRuleForm({
      id: row.plaidTransactionId,
      pattern: row.name || row.merchantName || '',
      category: row.finalCategory || row.suggestedCategory || 'other_outflow',
      priority: 50,
    });
  };
  const saveRule = async () => {
    if (!ruleForm?.pattern.trim()) return;
    try {
      await thirteenWeekCashFlowApi.createCategoryRule({
        pattern: ruleForm.pattern.trim(),
        category: ruleForm.category,
        priority: 50,
      });
      setRuleForm(null);
      setRuleError('');
      onRefresh(filters);
    } catch (requestError) {
      setRuleError(
        requestError?.response?.data?.error ||
          requestError?.message ||
          'Category rule could not be created',
      );
    }
  };
  if (loading && !data)
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        Loading cash activity…
      </div>
    );
  if (error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {error}
      </div>
    );
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-[145px] flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              From
            </span>
            <input
              type="date"
              value={filters.from}
              onChange={(event) =>
                setFilters({ ...filters, from: event.target.value })
              }
              className="h-8 w-full rounded-md border-gray-200 text-sm"
            />
          </label>
          <label className="min-w-[145px] flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              To
            </span>
            <input
              type="date"
              value={filters.to}
              onChange={(event) =>
                setFilters({ ...filters, to: event.target.value })
              }
              className="h-8 w-full rounded-md border-gray-200 text-sm"
            />
          </label>
          <label className="min-w-[150px] flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              State
            </span>
            <select
              value={filters.state}
              onChange={(event) =>
                setFilters({ ...filters, state: event.target.value })
              }
              className="h-8 w-full rounded-md border-gray-200 text-sm"
            >
              <option value="">All states</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="corrected">Corrected</option>
              <option value="excluded">Excluded</option>
            </select>
          </label>
          <label className="min-w-[180px] flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              Category
            </span>
            <select
              value={filters.category}
              onChange={(event) =>
                setFilters({ ...filters, category: event.target.value })
              }
              className="h-8 w-full rounded-md border-gray-200 text-sm"
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-[220px] flex-[2]">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              Search
            </span>
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters({ ...filters, search: event.target.value })
              }
              className="h-8 w-full rounded-md border-gray-200 text-sm"
              placeholder="Description or merchant"
            />
          </label>
          <span className="rounded-sm bg-orange-50 px-2 py-1.5 text-[11px] font-medium text-orange-700">
            {data?.uncategorizedCount || 0} uncategorized
          </span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-[1000px] w-full text-sm">
          <thead className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={
                    selected.length > 0 && selected.length === rows.length
                  }
                  onChange={(event) =>
                    setSelected(
                      event.target.checked
                        ? rows.map((row) => row.plaidTransactionId)
                        : [],
                    )
                  }
                />
              </th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Account</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Suggested</th>
              <th className="px-3 py-2">Final category</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <React.Fragment key={row.plaidTransactionId}>
                <tr className="border-t border-gray-100 hover:border-gray-200">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(row.plaidTransactionId)}
                      onChange={(event) =>
                        setSelected(
                          event.target.checked
                            ? [...selected, row.plaidTransactionId]
                            : selected.filter(
                                (id) => id !== row.plaidTransactionId,
                              ),
                        )
                      }
                    />
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {dateLabel(row.date)}
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-900">
                      {row.name || '—'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {row.merchantName || ''}
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    {row.accountName || '—'} {row.mask ? `···${row.mask}` : ''}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${Number(row.amount) < 0 ? 'text-emerald-700' : 'text-red-700'}`}
                  >
                    {money(row.amount)}
                  </td>
                  <td className="px-3 py-2">
                    <div>{row.suggestedCategory || '—'}</div>
                    <div className="mt-1 h-1 w-24 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-gray-400"
                        style={{
                          width: `${Math.max(0, Math.min(100, Number(row.confidence || 0) * 100))}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-400">
                      {Math.round(Number(row.confidence || 0) * 100)}%
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={row.finalCategory || row.suggestedCategory || ''}
                        onChange={(event) =>
                          update(row, {
                            finalCategory: event.target.value,
                            reviewState: 'corrected',
                          })
                        }
                        className="h-8 rounded-md border-gray-200 text-xs"
                      >
                        <option value="">Select</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => openRuleForm(row)}
                        className="text-xs text-blue-700 hover:underline"
                      >
                        Create rule
                      </button>
                    </div>
                    {row.split?.length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-gray-500">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium">
                          split
                        </span>
                        {row.billcomVendors?.map((vendor, index) => (
                          <span
                            key={`${vendor.vendorId || vendor.vendorName}-${index}`}
                          >
                            {vendor.vendorName || vendor.vendorId}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() =>
                        update(row, {
                          finalCategory:
                            row.finalCategory || row.suggestedCategory,
                          reviewState:
                            row.reviewState === 'confirmed'
                              ? 'pending'
                              : 'confirmed',
                        })
                      }
                    >
                      <Chip
                        tone={
                          row.reviewState === 'confirmed'
                            ? 'success'
                            : row.reviewState === 'excluded'
                              ? 'critical'
                              : 'neutral'
                        }
                      >
                        {row.reviewState || 'pending'}
                      </Chip>
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      defaultValue={row.note || ''}
                      onBlur={(event) =>
                        update(row, {
                          note: event.target.value,
                          reviewState: row.reviewState || 'pending',
                        })
                      }
                      className="h-8 w-40 rounded border-gray-200 text-xs"
                      placeholder="Add note"
                    />
                  </td>
                </tr>
                {ruleForm?.id === row.plaidTransactionId && (
                  <tr className="border-t border-blue-100 bg-blue-50/40">
                    <td colSpan={9} className="px-3 py-3">
                      <div className="grid items-end gap-2 md:grid-cols-[2fr_1fr_100px_auto_auto]">
                        <label className="text-xs text-gray-600">
                          Pattern
                          <input
                            value={ruleForm.pattern}
                            onChange={(event) =>
                              setRuleForm({
                                ...ruleForm,
                                pattern: event.target.value,
                              })
                            }
                            className="mt-1 h-8 w-full rounded border-gray-200 text-sm"
                          />
                        </label>
                        <label className="text-xs text-gray-600">
                          Category
                          <select
                            value={ruleForm.category}
                            onChange={(event) =>
                              setRuleForm({
                                ...ruleForm,
                                category: event.target.value,
                              })
                            }
                            className="mt-1 h-8 w-full rounded border-gray-200 text-xs"
                          >
                            {categories.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={saveRule}
                          className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRuleForm(null);
                            setRuleError('');
                          }}
                          className="h-8 rounded border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                      {ruleError && (
                        <p className="mt-2 text-xs text-red-700">{ruleError}</p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="px-4 py-8 text-center text-sm text-gray-600">
            No transactions match the current review filters.
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="fixed bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
          <span className="text-sm text-gray-700">
            {selected.length} selected
          </span>
          <button
            type="button"
            onClick={async () => {
              await thirteenWeekCashFlowApi.bulkUpdateTransactions({
                ids: selected,
                reviewState: 'confirmed',
              });
              setSelected([]);
              onRefresh(filters);
            }}
            className="h-8 rounded-md bg-black px-3 text-xs font-medium text-white"
          >
            Confirm
          </button>
          <select
            defaultValue=""
            onChange={async (event) => {
              if (!event.target.value) return;
              await thirteenWeekCashFlowApi.bulkUpdateTransactions({
                ids: selected,
                finalCategory: event.target.value,
                reviewState: 'corrected',
              });
              setSelected([]);
              onRefresh(filters);
            }}
            className="h-8 rounded border-gray-200 text-xs"
          >
            <option value="">Set category</option>
            {categories
              .filter(
                (category) => !['transfer', 'uncategorized'].includes(category),
              )
              .map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
          </select>
          <button
            type="button"
            onClick={async () => {
              await thirteenWeekCashFlowApi.bulkUpdateTransactions({
                ids: selected,
                reviewState: 'excluded',
                finalCategory: 'excluded',
              });
              setSelected([]);
              onRefresh(filters);
            }}
            className="h-8 rounded-md border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700"
          >
            Exclude
          </button>
        </div>
      )}
    </div>
  );
}
